import { useState } from 'react';
import { format, isFuture, isValid, parse } from 'date-fns';

import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  DateInput,
  DatePicker,
  FormField,
  FormHelp,
  Heading,
  Label,
  Modal,
  Paragraph,
  Text,
  Textarea,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';

import { CharacterCountFormHelp, useCharacterCount } from '@zg-rentals/visual-cohesion';
import { getBooleanConfig } from '@zg-rentals/frontend-config/client';
import {
  useMarkOneTimePaymentAsPaidOfflineWebApiHandler_markOneTimePaymentAsPaidOffline,
  useMarkPaymentAsPaidOfflineWebApiHandler_markPaymentAsPaidOffline,
} from '@zg-rentals/ts-rent-payment-client-react-query';

import { getIreneDashboard } from '@rental-manager/actions/requests';
import { useAppDispatch } from '../../../../../../../../store/hooks';

const NOTE_MAX_LENGTH = 1000;

type MarkAsPaidTarget =
  | { kind: 'rent'; rentTermsKey: string; scheduleMonth: string }
  | { kind: 'oneTimePayment'; groupKey: string };

type Props = {
  heading: string;
  subheading: string;
  amount: string;
  propertyId: string;
  target: MarkAsPaidTarget;
};

const MarkAsPaidModalContent = ({
  heading,
  subheading,
  amount,
  propertyId,
  target,
  onClose,
}: Props & { onClose: () => void }) => {
  const dispatch = useAppDispatch();
  const { enqueueToast } = useToast();
  const rentMutation = useMarkPaymentAsPaidOfflineWebApiHandler_markPaymentAsPaidOffline({
    meta: {
      invalidates: ['landlordTransactions'],
      context: {
        api: 'MarkPaymentAsPaidOfflineWebApiHandler_markPaymentAsPaidOffline',
        details: 'single-property dashboard mark as paid',
      },
    },
  });
  const oneTimePaymentMutation = useMarkOneTimePaymentAsPaidOfflineWebApiHandler_markOneTimePaymentAsPaidOffline({
    meta: {
      invalidates: ['landlordTransactions'],
      context: {
        api: 'MarkOneTimePaymentAsPaidOfflineWebApiHandler_markOneTimePaymentAsPaidOffline',
        details: 'single-property dashboard mark as paid',
      },
    },
  });
  const isPending = rentMutation.isPending || oneTimePaymentMutation.isPending;
  const [note, setNote] = useState('');
  const { isFocused, onFocus, onBlur } = useCharacterCount();
  const isNoteOverLimit = note.length > NOTE_MAX_LENGTH;
  const [dateReceived, setDateReceived] = useState('');
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const strictlyParsedDate = dateReceived ? parse(dateReceived, 'MM/dd/yyyy', new Date()) : null;
  const parsedDateReceived = strictlyParsedDate && isValid(strictlyParsedDate) ? strictlyParsedDate : undefined;
  const dateError =
    dateReceived && !parsedDateReceived
      ? 'Enter a valid date'
      : parsedDateReceived && parsedDateReceived.getFullYear() < 2000
        ? 'Enter a date on or after Jan 1, 2000'
        : parsedDateReceived && isFuture(parsedDateReceived)
          ? 'Enter a date on or before today'
          : '';

  const handleSubmit = () => {
    if (!parsedDateReceived || dateError || isNoteOverLimit) {
      return;
    }
    const dateReceivedDto = {
      year: parsedDateReceived.getFullYear(),
      month: parsedDateReceived.getMonth() + 1,
      day: parsedDateReceived.getDate(),
    };
    const callbacks = {
      onSuccess: () => {
        dispatch(getIreneDashboard({ propertyId }));
        enqueueToast(<Toast tone="success">Payment recorded</Toast>);
        onClose();
      },
      onError: () => {
        enqueueToast(<Toast tone="critical">Payment couldn’t be recorded. Try again.</Toast>);
      },
    };
    if (target.kind === 'rent') {
      rentMutation.mutate(
        {
          postData: {
            dateReceived: dateReceivedDto,
            notes: note,
            rentTermsKey: target.rentTermsKey,
            resourceId: propertyId,
            scheduleMonthForPayment: target.scheduleMonth,
          },
        },
        callbacks,
      );
    } else {
      oneTimePaymentMutation.mutate(
        {
          postData: {
            dateReceived: dateReceivedDto,
            notes: note,
            groupKey: target.groupKey,
          },
        },
        callbacks,
      );
    }
  };

  return (
    <Modal
      onOpenChange={(isOpen) => !isOpen && onClose()}
      open={true}
      size={{ base: 'fullScreen', md: 'md' }}
      header={<Heading level={2}>Mark as paid</Heading>}
      body={
        <>
          <Paragraph css={{ marginBottom: 'default' }}>
            {target.kind === 'rent'
              ? `Add a payment you received outside Zillow. Once recorded, it will be added to your transaction history and your renter’s past-due status will clear automatically.`
              : `Add a payment you received outside Zillow. Once recorded, it will be added to your transaction history and this request will be marked as paid.`}
          </Paragraph>

          <Card elevated={false} css={{ marginBottom: 'default' }}>
            <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'default' }}>
              <Box css={{ display: 'flex', flexDirection: 'column' }}>
                <Text textStyle="body-sm-bold">{heading}</Text>
                <Text textStyle="body-sm">{subheading}</Text>
              </Box>
              <Text textStyle="body-lg-bold">{amount}</Text>
            </Box>
          </Card>

          <FormField
            css={{ marginBottom: 'default' }}
            required
            error={!!dateError}
            label={<Label>Date received</Label>}
            description={<FormHelp>{dateError || 'MM/DD/YYYY'}</FormHelp>}
            control={
              <DatePicker
                closeModalOnDateSelect
                dateFormat="MM/dd/yyyy"
                disabledDates={(date) => isFuture(date)}
                error={!!dateError}
                open={isCalendarOpen}
                onOpenChange={setIsCalendarOpen}
                value={dateReceived}
                onValueChange={(value) =>
                  setDateReceived(typeof value === 'string' ? value : format(value, 'MM/dd/yyyy'))
                }
                input={<DateInput css={{ paddingLeft: 'default' }} />}
              />
            }
          />

          <FormField
            css={{ marginBottom: 'default' }}
            optional
            error={isNoteOverLimit}
            label={<Label>Note</Label>}
            description={
              <CharacterCountFormHelp
                currentLength={note.length}
                maxLength={NOTE_MAX_LENGTH}
                isFocused={isFocused}
                label="Note"
              />
            }
            control={
              <Textarea
                value={note}
                onChange={(event) => setNote(event.target.value)}
                onFocus={onFocus}
                onBlur={onBlur}
              />
            }
          />

          <Alert tone="warning">
            You’ll see this payment on your Zillow dashboard, but it won’t appear on your Stripe-issued 1099.
          </Alert>
        </>
      }
      footer={
        <ButtonGroup aria-label="Mark as paid actions">
          <TextButton onClick={onClose}>Cancel</TextButton>

          <Button
            tone="brand"
            emphasis="filled"
            onClick={handleSubmit}
            loading={isPending}
            disabled={!parsedDateReceived || !!dateError || isNoteOverLimit}
          >
            Mark as paid
          </Button>
        </ButtonGroup>
      }
    />
  );
};

const MarkAsPaidAction = (props: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!getBooleanConfig('isLandlordMarkAsPaidOfflineEnabled', false)) {
    return null;
  }

  return (
    <>
      <Button size="sm" onClick={() => setIsOpen(true)}>
        Mark as paid
      </Button>
      {isOpen && <MarkAsPaidModalContent {...props} onClose={() => setIsOpen(false)} />}
    </>
  );
};

export default MarkAsPaidAction;
